/* PCIS Health — Manage (route /supply/pcis-health/manage). Lists every Direct
   Integration (PCIS) courier account in a table. The one action here is switching
   an account's integration_type:
     NATIVE            → orders placed directly on the courier's PCIS integration
     CLICKPOST_FALLBACK → orders routed via Click Post instead
   Toggling calls POST pcis-admin/courier-accounts/{accountCode}/integration-type
   ?value=<NATIVE|CLICKPOST_FALLBACK> (no body). The backend evicts its cache, so
   the change takes effect on the next order; the mutation invalidates the accounts
   list so the table reflects the new value immediately. */
import React, { useMemo, useState } from 'react'
import { Box, Stack, Switch, Tooltip, Typography, Button } from '@mui/material'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import PageTitle from '../../../components/PageTitle'
import { PageHeader, Badge, KpiStrip, SearchSelect, TableSkeleton, LoadErrorPanel, ConfirmDialog, useToast } from '../components'
import ProzoDataGrid from '../../../common/ProzoDataGrid'
import { useListPcisCourierAccountsQuery, useSwitchAccountIntegrationTypeMutation } from '../../../redux/supplyApiSlice'
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined'
import PcisAddAccountModal from './PcisAddAccountModal'
import PcisEditCredentialsDrawer from './PcisEditCredentialsDrawer'

const NATIVE = 'NATIVE'
const CLICKPOST = 'CLICKPOST_FALLBACK'

const TYPE_LABEL = {
  [NATIVE]: 'PCIS',
  [CLICKPOST]: 'Clickpost',
}

/* Per-row integration-type control. ON = NATIVE (direct PCIS), OFF = Click Post
   fallback. Clicking the switch opens a confirmation (this changes live order
   routing); the API fires only on confirm. Each row owns its own mutation state
   so only the row being changed shows a pending/disabled switch. */
function IntegrationTypeToggle({ account }) {
  const toast = useToast()
  const [switchType, { isLoading }] = useSwitchAccountIntegrationTypeMutation()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const isNative = String(account.integration_type).toUpperCase() === NATIVE
  const target = isNative ? CLICKPOST : NATIVE

  const doSwitch = async () => {
    try {
      await switchType({ accountCode: account.account_code, value: target }).unwrap()
      toast(`${account.account_code} switched to ${TYPE_LABEL[target]}.`, 'success')
    } catch (err) {
      toast(err?.message || 'Could not switch integration type.', 'error')
    }
    setConfirmOpen(false)
  }

  return (
    <>
      {/* Single onClick target (like CopyIcon) — MUI Switch's own onChange is
          unreliable inside a DataGrid cell, so the Switch is visual-only
          (pointerEvents:none) and the wrapping Box owns the click. */}
      <Stack
        direction='row'
        alignItems='center'
        spacing={0.8}
        onClick={(e) => {
          e.stopPropagation()
          if (!isLoading) setConfirmOpen(true)
        }}
        sx={{ cursor: isLoading ? 'default' : 'pointer' }}
      >
        <Tooltip title='Clickpost'>
          <Typography sx={{ fontSize: 11, fontWeight: isNative ? 400 : 700, color: isNative ? 'text.disabled' : 'text.primary' }}>Clickpost</Typography>
        </Tooltip>
        <Switch
          size='small'
          checked={isNative}
          disabled={isLoading}
          readOnly
          tabIndex={-1}
          sx={{ pointerEvents: 'none' }}
        />
        <Tooltip title='PCIS (direct integration)'>
          <Typography sx={{ fontSize: 11, fontWeight: isNative ? 700 : 400, color: isNative ? 'text.primary' : 'text.disabled' }}>PCIS</Typography>
        </Tooltip>
      </Stack>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title='Switch integration type?'
        okLabel={`Switch to ${TYPE_LABEL[target]}`}
        danger={target === CLICKPOST}
        body={`Switch “${account.account_code}” (${account.courier_code}) from ${TYPE_LABEL[isNative ? NATIVE : CLICKPOST]} to ${TYPE_LABEL[target]}?`}
        bullets={[
          target === NATIVE ? 'Orders will be placed directly on the courier’s PCIS integration.' : 'Orders will be routed via Click Post instead of PCIS.',
          'Takes effect on the next order (cache is cleared immediately).',
        ]}
        onOk={doSwitch}
      />
    </>
  )
}

const COLUMNS = [
  {
    field: 'account_code',
    headerName: 'Courier & Account',
    minWidth: 240,
    flex: 1.2,
    sortable: false,
    renderCell: ({ row }) => (
      <Box sx={{ py: 0.5 }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.3 }}>{row.courier_code || '—'}</Typography>
        <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.3, wordBreak: 'break-all' }}>{row.account_code}</Typography>
      </Box>
    ),
  },
  {
    field: 'cp_id',
    headerName: 'CP ID',
    flex: 0.8,
    minWidth: 90,
    sortable: false,
    renderCell: ({ row }) => <Typography sx={{ fontSize: 12.5 }}>{row.cp_id ?? '—'}</Typography>,
  },
  {
    field: 'is_active',
    headerName: 'Status',
    minWidth: 110,
    flex: 1,
    sortable: false,
    renderCell: ({ row }) => <Badge status={row.is_active ? 'active' : 'inactive'} />,
  },
  {
    field: 'integration_type',
    headerName: 'Integration Type',
    minWidth: 190,
    flex: 1,
    sortable: false,
    renderCell: ({ row }) => <IntegrationTypeToggle account={row} />,
  },
]

export default function PcisHealthManage() {
  const [courier, setCourier] = useState('')
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(20)
  const [addOpen, setAddOpen] = useState(false)
  const [editAccount, setEditAccount] = useState(null)
  const { data: accounts, isFetching, isError, error, refetch } = useListPcisCourierAccountsQuery()

  /* base columns + an Edit-credentials action (defined here so it can open the drawer) */
  const columns = useMemo(
    () => [
      ...COLUMNS,
      {
        field: '_edit',
        headerName: 'Credentials',
        minWidth: 140,
        sortable: false,
        renderCell: ({ row }) => (
          <Button size='small' variant='outlined' startIcon={<KeyOutlinedIcon />} onClick={() => setEditAccount(row)} sx={{ borderRadius: '9px', textTransform: 'none', fontWeight: 600, fontSize: 12.5 }}>
            Edit
          </Button>
        ),
      },
    ],
    [],
  )

  const courierOptions = useMemo(() => {
    const codes = Array.from(new Set((accounts || []).map((a) => a.courier_code).filter(Boolean)))
    return codes.map((c) => ({ value: c, label: c }))
  }, [accounts])

  const rows = useMemo(
    () => (courier ? (accounts || []).filter((a) => a.courier_code === courier) : accounts || []),
    [accounts, courier],
  )

  const stats = useMemo(() => {
    const all = accounts || []
    const isNative = (a) => String(a.integration_type).toUpperCase() === NATIVE
    return {
      total: all.length,
      couriers: new Set(all.map((a) => a.courier_code).filter(Boolean)).size,
      pcis: all.filter(isNative).length,
      clickpost: all.filter((a) => !isNative(a)).length,
      active: all.filter((a) => a.is_active).length,
    }
  }, [accounts])

  const kpiCards = [
    { label: 'Total Accounts', value: stats.total.toLocaleString(), sub: `${stats.couriers} couriers`, icon: <AccountTreeOutlinedIcon />, accent: '#2065D1' },
    { label: 'On PCIS', value: stats.pcis.toLocaleString(), sub: 'Direct integration', icon: <BoltOutlinedIcon />, accent: '#0EA5A4' },
    { label: 'On Clickpost', value: stats.clickpost.toLocaleString(), sub: 'Fallback routing', icon: <SwapHorizOutlinedIcon />, accent: '#8B5CF6' },
    { label: 'Active', value: stats.active.toLocaleString(), sub: `${stats.total - stats.active} inactive`, icon: <CheckCircleOutlineIcon />, accent: '#16A34A' },
  ]

  return (
    <>
      <PageTitle>PCIS — Manage</PageTitle>
      <PageHeader
        title='PCIS — Manage Accounts'
        sub='Direct Integration (PCIS) courier accounts. Toggle an account between Native (direct PCIS) and Click Post routing.'
        actions={
          <Button
            variant='contained'
            disableElevation
            startIcon={<AddOutlinedIcon />}
            onClick={() => setAddOpen(true)}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: 13.5,
              px: 2.25,
              py: 1,
              color: '#fff',
              backgroundImage: 'linear-gradient(135deg, #4F7DF0 0%, #2065D1 100%)',
              boxShadow: '0 6px 16px rgba(32,101,209,.35)',
              transition: 'transform .15s ease, box-shadow .15s ease, filter .15s ease',
              '&:hover': { backgroundImage: 'linear-gradient(135deg, #4F7DF0 0%, #2065D1 100%)', filter: 'brightness(1.05)', boxShadow: '0 10px 22px rgba(32,101,209,.45)', transform: 'translateY(-1px)' },
              '& .MuiButton-startIcon': { mr: 0.75 },
            }}
          >
            Add account
          </Button>
        }
      />

      <PcisAddAccountModal open={addOpen} onClose={() => setAddOpen(false)} />
      <PcisEditCredentialsDrawer open={!!editAccount} account={editAccount} onClose={() => setEditAccount(null)} />

      {isFetching ? (
        <TableSkeleton kpis={4} rows={4} />
      ) : isError ? (
        <LoadErrorPanel message={error?.message || 'Could not load courier accounts.'} onRetry={refetch} />
      ) : (
        <>
          <KpiStrip cards={kpiCards} />

          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: '16px', bgcolor: 'background.paper', overflow: 'hidden', boxShadow: '0 1px 2px rgba(15,23,42,.04)' }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ sm: 'center' }}
              justifyContent='space-between'
              spacing={1.5}
              sx={{ px: 2.25, py: 1.75, borderBottom: 1, borderColor: 'divider' }}
            >
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'text.primary' }}>Courier Accounts</Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  {rows.length} {rows.length === 1 ? 'account' : 'accounts'}
                  {courier ? ` · ${courier}` : ''}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 220 }}>
                <SearchSelect
                  placeholder='All couriers'
                  value={courier}
                  onChange={(v) => {
                    setCourier(v)
                    setPage(0)
                  }}
                  options={courierOptions}
                />
              </Box>
            </Stack>

            <ProzoDataGrid
              columns={columns}
              rows={rows}
              getRowId={(r) => r.id || r.account_code}
              rowCount={rows.length}
              paginationMode='client'
              page={page}
              setPage={setPage}
              sizePerPage={sizePerPage}
              setSizePerPage={setSizePerPage}
              pagination
            />
          </Box>
        </>
      )}
    </>
  )
}
